import React, { useEffect, useRef, useState } from 'react';
import KeywordFormSection from './components/KeywordFormSection';
import styles from './HighlightsModal.module.scss';
import { errorMessages } from './components/validateKeyword';
import {
  Button,
  Message,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from 'components/core';
import { Inline, Stack } from 'components/layout';

const getAPIErrorMessage = (messages: string[]) => {
  if (messages.includes('must be unique')) {
    return errorMessages['duplicateKeyword'];
  }

  return 'Error saving changes. Please try again or refresh the page.';
};

type HighlightsModalProps = {
  isOpen: boolean;
  includedKeywords: string[];
  excludedKeywords: string[];
  handleClose: () => void;
  saveReviewData: (data: Record<string | number, unknown>) => Promise<any>;
};

const HighlightsModal = ({
  isOpen,
  handleClose,
  saveReviewData,
  includedKeywords,
  excludedKeywords,
}: HighlightsModalProps) => {
  const [apiErrorMessages, setApiErrorMessages] = useState<string[] | null>(
    null
  );
  const errorRef = useRef<HTMLDivElement | null>(null);
  const hasAPIError = Boolean(apiErrorMessages?.length);

  const onUpdateInclusionKeyword = (updatedWordList: string[]) =>
    saveReviewData({ included_keywords: updatedWordList });

  const onUpdateExclusionKeyword = (updatedWordList: string[]) =>
    saveReviewData({ excluded_keywords: updatedWordList });

  useEffect(() => {
    if (hasAPIError) {
      errorRef.current?.focus();
    }
  }, [hasAPIError]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      aria-label="Manage Highlights"
    >
      <ModalHeader>Manage Highlights</ModalHeader>
      <ModalBody>
        <Stack spacing={5}>
          <Text variant="neutral">
            Highlights help to quickly spot words or phrases during screening by
            automatically highlighting inclusion words in green and exclusion
            words in red.
          </Text>

          <Inline alignY="top" shouldWrap spacing={5}>
            <KeywordFormSection
              title="Inclusion"
              titleColor="success"
              label="Add inclusion word"
              keywords={includedKeywords}
              onUpdate={onUpdateInclusionKeyword}
              onApiError={(errors) => {
                setApiErrorMessages(errors);
              }}
            />

            <KeywordFormSection
              title="Exclusion"
              titleColor="danger"
              label="Add exclusion word"
              keywords={excludedKeywords}
              onUpdate={onUpdateExclusionKeyword}
              onApiError={(errors) => {
                setApiErrorMessages(errors);
              }}
            />
          </Inline>
        </Stack>

        <div role="alert" ref={errorRef} tabIndex={-1}>
          {hasAPIError && (
            <Message
              className={styles.apiErrorMessage}
              type="danger"
              variant="prominent"
              isVisible={hasAPIError}
              onClose={() => setApiErrorMessages(null)}
            >
              <Text>{getAPIErrorMessage(apiErrorMessages || [])}</Text>
            </Message>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button type="brand" onClick={handleClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default HighlightsModal;
